<template>
  <div class="display-control-panel">
    <!-- Connection widget -->
    <div class="dcp-widget dcp-connection" v-if="shouldShowWidget('connection')">
      <div class="dcp-connection__row">
        <span class="dcp-connection__status" v-if="connected_clients">
          <span class="dcp-status-dot"></span>
          {{ connected_clients - 1 }} screen{{ connected_clients - 1 !== 1 ? 's' : '' }}
        </span>
        <span class="dcp-connection__status dcp-connection__status--off" v-else>
          <span class="dcp-status-dot dcp-status-dot--off"></span>
          No screens
        </span>
        <div class="dcp-connection__actions">
          <button v-if="!gameConfig.auto_sync" class="dcp-link-btn" :disabled="sync_disabled"
                  @click="autoSync()">
            <i class="tim-icons icon-refresh-02"></i>
          </button>
          <button class="dcp-link-btn" @click="copyURL()">
            <i class="tim-icons icon-single-copy-04"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Timer widget -->
    <div class="dcp-widget dcp-timer-widget" v-if="shouldShowWidget('timer') && (gameConfig.has_timer || showStartGameButton || showCompleteGameButton)">
      <!-- Timer face -->
      <div v-if="gameConfig.has_timer && displaySettings.display_timer !== false"
           class="dcp-timer-face" :class="{ 'dcp-timer-face--running': timerEnabled }">
        <div class="dcp-timer-digits">
          <span class="dcp-timer-segment">{{ hours }}</span>
          <span class="dcp-timer-colon">:</span>
          <span class="dcp-timer-segment">{{ minutes }}</span>
          <span class="dcp-timer-colon">:</span>
          <span class="dcp-timer-segment">{{ seconds }}</span>
        </div>
        <span class="dcp-timer-mode">{{ countUpMode ? 'Elapsed' : 'Remaining' }}</span>
      </div>

      <!-- Start event button (when intro media is configured) -->
      <div class="dcp-timer-controls" v-if="!time_override && showStartGameButton && !gameStarted">
        <button class="dcp-timer-btn dcp-timer-btn--play dcp-timer-btn--start-game"
                @click="startGame()">
          <i class="tim-icons icon-triangle-right-17"></i>
          Start event
        </button>
      </div>

      <!-- Main controls -->
      <div class="dcp-timer-controls" v-if="showTimer && !time_override && (!showStartGameButton || gameStarted)">
        <button class="dcp-timer-btn dcp-timer-btn--play"
                :class="{ active: timerEnabled }"
                @click="timerEnabled ? pauseTimer() : startTimer()">
          <i class="tim-icons" :class="timerEnabled ? 'icon-button-pause' : 'icon-triangle-right-17'"></i>
          {{ timerEnabled ? 'Pause' : 'Start' }}
        </button>
        <button class="dcp-timer-btn" @click="resetTimer()">
          <i class="tim-icons icon-refresh-01"></i>
          Reset
        </button>
      </div>

      <!-- Secondary actions -->
      <div class="dcp-timer-secondary" v-if="showTimer && !time_override && (!showStartGameButton || gameStarted)">
        <button class="dcp-text-btn" @click="toggleTimer(show_timer ? 0 : 1)">
          <i class="tim-icons" :class="show_timer ? 'icon-simple-remove' : 'icon-tv-2'"></i>
          {{ show_timer ? 'Hide on screen' : 'Show on screen' }}
        </button>
        <button class="dcp-text-btn" @click="time_override = true">
          <i class="tim-icons icon-pencil"></i>
          Override
        </button>
      </div>

      <!-- Time adjustment buttons -->
      <div class="dcp-timer-adjust" v-if="showTimer && !time_override && displaySettings.display_time_adjustment && (!showStartGameButton || gameStarted)">
        <span class="dcp-adjust-title">Adjust time</span>
        <div class="dcp-adjust-row">
          <button class="dcp-adjust-btn dcp-adjust-btn--minus"
                  v-for="val in sortedIntervals.slice().reverse()" :key="'sub' + val"
                  @click="adjustTimer(-val)">-{{ val }}</button>
          <span class="dcp-adjust-divider"></span>
          <button class="dcp-adjust-btn dcp-adjust-btn--plus"
                  v-for="val in sortedIntervals" :key="'add' + val"
                  @click="adjustTimer(val)">+{{ val }}</button>
        </div>
      </div>

      <!-- Timer speed controls -->
      <div class="dcp-timer-speed" v-if="showTimer && !time_override && displaySettings.display_timer_speed && (!showStartGameButton || gameStarted)">
        <span class="dcp-speed-title">Speed</span>
        <div class="dcp-speed-row">
          <button v-for="speed in [0.5, 0.8, 1, 1.2, 1.5, 2]" :key="'spd' + speed"
                  class="dcp-speed-btn"
                  :class="{ 'dcp-speed-btn--active': timerSpeed === speed }"
                  @click="setTimerSpeed(speed)">{{ speed }}x</button>
        </div>
      </div>

      <!-- Complete event button -->
      <div class="dcp-timer-controls" v-if="!time_override && showCompleteGameButton && (!showStartGameButton || gameStarted) && !gameCompleted">
        <button class="dcp-timer-btn dcp-timer-btn--complete"
                @click="confirmCompleteGame()">
          <i class="tim-icons icon-check-2"></i>
          Complete event
        </button>
      </div>

      <!-- Override input -->
      <div class="dcp-timer-override" v-if="showTimer && time_override">
        <label class="dcp-timer-override__label">Set time (minutes)</label>
        <div class="dcp-timer-override__row">
          <base-input v-model="timer_override_value" type="number" class="mb-0"></base-input>
          <button class="dcp-timer-btn dcp-timer-btn--play" @click="overrideTimer()">Set</button>
          <button class="dcp-text-btn" @click="time_override = false">Cancel</button>
        </div>
      </div>
    </div>

    <!-- Background music widget -->
    <div class="dcp-widget" v-if="shouldShowWidget('music') && gameConfig.play_background_music && displaySettings.display_background_music !== false">
      <div class="dcp-music-row">
        <span class="dcp-widget__title mb-0">
          <i class="tim-icons icon-note-03 mr-1"></i>Background music
        </span>
        <button class="dcp-timer-btn"
                :class="{ 'dcp-timer-btn--play active': background_playing }"
                @click="background_playing ? pauseBackgroundMusic() : playBackgroundMusic()">
          <i class="tim-icons" :class="background_playing ? 'icon-button-pause' : 'icon-triangle-right-17'"></i>
          {{ background_playing ? 'Pause' : 'Play' }}
        </button>
      </div>
    </div>

    <!-- Send custom interaction -->
    <div class="dcp-widget" v-if="shouldShowWidget('custom') && displaySettings.display_custom_interaction !== false">
      <h4 class="dcp-widget__title">Send custom interaction</h4>
      <textarea
        class="dcp-hint-textarea"
        v-model="custom_clue"
        rows="3"
        placeholder="Type a message to display..."
      ></textarea>
      <div class="dcp-hint-footer">
        <button class="dcp-timer-btn dcp-timer-btn--play"
                :disabled="!custom_clue || !custom_clue.trim()"
                @click="sendCustomClue()">
          <i class="tim-icons icon-send"></i>
          Send
        </button>
      </div>
    </div>

    <!-- Hint counter -->
    <div class="dcp-widget dcp-hint-counter-widget" v-if="shouldShowWidget('hints') && gameConfig.has_clue_limit && displaySettings.display_hint_counter !== false" :key="'Z' + h">
      <div class="dcp-hint-counter-header">
        <h4 class="dcp-widget__title mb-0">Hints</h4>
        <span class="dcp-hint-count-label">{{ clues_used }} / {{ clues_used + clues_remaining }}</span>
      </div>
      <div class="dcp-hint-icons">
        <i v-for="i in clues_used" :key="i + 'M'"
           :class="'tim-icons ' + gameConfig.icon_style"
           class="dcp-hint-icon dcp-hint-icon--used"></i>
        <i v-for="j in clues_remaining" :key="j + 'N'"
           :class="'tim-icons ' + gameConfig.icon_style"
           class="dcp-hint-icon dcp-hint-icon--remaining"></i>
      </div>
      <div class="dcp-hint-counter-actions">
        <button class="dcp-link-btn" @click="removeExtraHints()">
          <i class="tim-icons el-icon-minus"></i>
        </button>
        <button class="dcp-text-btn" @click="takeBackHint()">Undo last</button>
        <button class="dcp-text-btn" @click="resetHints()">Reset</button>
        <button class="dcp-link-btn" @click="giveExtraHints()">
          <i class="tim-icons el-icon-plus"></i>
        </button>
      </div>
    </div>

    <!-- Currently displayed -->
    <div class="dcp-widget dcp-now-showing" v-if="shouldShowWidget('onscreen')">
      <div class="dcp-now-showing__header">
        <h4 class="dcp-widget__title mb-0">On screen</h4>
        <button v-if="!cleared && clues_sent.length > 0"
                class="dcp-text-btn" @click="clearHints()">
          <i class="tim-icons icon-simple-remove"></i> Clear
        </button>
      </div>
      <div class="dcp-now-showing__content" v-if="!cleared && clues_sent.length > 0">
        <span class="dcp-now-showing__label">
          {{ getDisplayedHintLabel(clues_sent[clues_sent.length - 1]) }}
        </span>
        <span v-if="clues_sent[clues_sent.length - 1].type === 'video' && video_looping"
              class="dcp-now-showing__badge">Looping</span>
      </div>
      <div class="dcp-now-showing__empty" v-else>
        Nothing displayed
      </div>
    </div>
  </div>
</template>

<script>
import swal from 'sweetalert2'

export default {
  name: "DisplayControlPanel",
  props: {
    socket: { default: null },
    gameConfig: { type: Object, default: () => ({}) },
    gameId: { type: [Number, String], default: null },
    displaySettings: { type: Object, default: () => ({}) },
    visibleWidgets: { type: Array, default: null }
  },
  data() {
    return {
      background_playing: false,
      show_timer: true,
      time_override: false,
      timer_override_value: '60',
      custom_clue: '',
      cleared: false,
      video_looping: false,
      connected_clients: 0,
      sync_disabled: false,
      clues_sent: [],
      clues_used: 0,
      clues_remaining: 0,
      h: 0,

      // Timer state
      hours: '00',
      minutes: '00',
      seconds: '00',
      totalSeconds: 0,
      originalTotalSeconds: 0,
      originalTotalMinutes: 0,
      timerEnabled: false,
      countUpMode: false,
      lastTime: null,
      timerId: null,

      gameStarted: false,
      gameCompleted: false,
      waitingForIntroVideo: false,

      timerSpeed: 1,

      // WebSocket listener reference for cleanup
      wsMessageHandler: null,
    }
  },
  watch: {
    socket(newSocket) {
      if (newSocket) {
        this.attachSocketListeners()
      }
    }
  },
  computed: {
    showStartGameButton() {
      return this.displaySettings.enable_start_game_button &&
        this.gameConfig.intro_video_url
    },
    showCompleteGameButton() {
      return this.displaySettings.enable_complete_game_button
    },
    showTimer() {
      return this.gameConfig.has_timer && this.displaySettings.display_timer !== false
    },
    sortedIntervals() {
      let intervals = this.displaySettings.time_adjustment_intervals || [1, 3, 5]
      if (typeof intervals === 'string') { intervals = JSON.parse(intervals) }
      return intervals.slice().sort((a, b) => a - b)
    }
  },
  methods: {
    startGame() {
      if (this.gameStarted) return
      this.gameStarted = true

      // Send intro media to display
      this.sendWsMessage('video', this.gameConfig.intro_video_url)

      if (this.gameConfig.intro_video_timer_mode === 'after_video') {
        // Wait for video to end before starting timer
        this.waitingForIntroVideo = true
      } else {
        // Start timer immediately alongside video
        this.startTimer()
      }
    },
    handleIntroVideoEnded() {
      if (this.waitingForIntroVideo) {
        this.waitingForIntroVideo = false
        this.startTimer()
      }
    },
    async confirmCompleteGame() {
      const result = await swal({
        title: 'Complete this event?',
        text: 'This will stop the timer and play the completion media.',
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Complete event',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#e14eca',
      })

      if (result.value) {
        this.completeGame()
      }
    },
    completeGame() {
      if (this.gameCompleted) return
      this.gameCompleted = true

      const completionMediaUrl = this.gameConfig.completion_media_url

      this.pauseTimer()

      // Stop background music if playing
      if (this.background_playing) {
        this.pauseBackgroundMusic()
      }

      // Send completion media to customer display
      if (completionMediaUrl) {
        this.sendWsMessage('completion_media', completionMediaUrl)
      }
    },
    markCompleted() {
      this.gameCompleted = true
    },
    // --- Timer ---
    initTimer(maxTime, countdownMode) {
      this.countUpMode = countdownMode === 'up'

      if (this.countUpMode) {
        this.totalSeconds = 0
      } else if (maxTime) {
        let parts = maxTime.split(':').map(n => parseInt(n, 10) || 0)
        this.totalSeconds = (parts[0] * 3600) + (parts[1] * 60) + (parts[2] || 0)
      }

      this.updateTimeDisplay()
      this.originalTotalSeconds = this.totalSeconds
      this.originalTotalMinutes = Math.ceil(this.totalSeconds / 60)
      this.lastTime = Date.now()

      // Init hint counter
      if (this.gameConfig.has_clue_limit) {
        this.clues_used = 0
        this.clues_remaining = parseInt(this.gameConfig.max_clues) || 0
      }
    },

    startTimer() {
      if (this.timerId) return
      this.timerEnabled = true
      this.lastTime = Date.now()
      this.timerId = requestAnimationFrame(this.updateTimer)

      // Broadcast to display — send sync first so display has current time,
      // then start so it begins counting from that value
      let syncData = {
        timer: this.hours + ':' + this.minutes + ':' + this.seconds,
        clues_used: this.clues_used,
        clues_remaining: this.clues_remaining,
        timer_active: true,
        show_timer: this.show_timer
      }
      this.sendWsMessage('sync', syncData)
      this.sendWsMessage('timer', 'start')
      this.$emit('timer-started')
    },

    pauseTimer() {
      if (this.timerId) {
        cancelAnimationFrame(this.timerId)
        this.timerId = null
      }
      this.timerEnabled = false
      this.sendWsMessage('timer', 'pause')
      this.$emit('timer-stopped')
    },

    resetTimer() {
      this.pauseTimer()
      this.gameStarted = false
      this.gameCompleted = false
      this.waitingForIntroVideo = false
      if (this.countUpMode) {
        this.totalSeconds = 0
      } else {
        this.totalSeconds = this.originalTotalMinutes * 60
      }
      this.updateTimeDisplay()
      this.sendWsMessage('timer', 'reset')

      if (this.timerSpeed !== 1) {
        this.timerSpeed = 1
        this.sendWsMessage('timer_speed', 1)
        this.$emit('timer-speed-changed', 1)
      }
    },

    updateTimer() {
      if (!this.timerEnabled) return

      // Date.now() (not performance.now()) so time lost to device sleep or
      // page suspension is caught up on the next frame instead of dropped
      let now = Date.now()
      let elapsedMs = now - this.lastTime
      let elapsedTime = Math.floor((elapsedMs * this.timerSpeed) / 1000)

      if (elapsedTime !== 0) {
        if (this.countUpMode) {
          this.totalSeconds += elapsedTime
        } else {
          this.totalSeconds -= elapsedTime
        }
        // Advance by the consumed whole seconds only, keeping the sub-second
        // remainder so the clock doesn't lose a fraction on every tick
        this.lastTime += (elapsedTime * 1000) / this.timerSpeed

        // Countdown stops at zero, matching the customer display, which
        // never shows negative time. The objectives timer keeps the true
        // elapsed amount.
        if (!this.countUpMode && this.totalSeconds <= 0) {
          this.totalSeconds = 0
          this.updateTimeDisplay()
          this.timerId = null
          this.timerEnabled = false
          this.$emit('timer-stopped')
          return
        }

        this.updateTimeDisplay()
      }

      this.timerId = requestAnimationFrame(this.updateTimer)
    },

    updateTimeDisplay() {
      let clamped = Math.max(0, this.totalSeconds)

      let h = Math.floor(clamped / 3600)
      let m = Math.floor((clamped % 3600) / 60)
      let s = clamped % 60

      this.hours = String(h).padStart(2, '0')
      this.minutes = String(m).padStart(2, '0')
      this.seconds = String(s).padStart(2, '0')
    },

    stopTimer() {
      if (this.timerId) {
        cancelAnimationFrame(this.timerId)
        this.timerId = null
      }
      this.timerEnabled = false
    },

    toggleTimer(status) {
      if (status === 1) {
        this.show_timer = true
        this.sendWsMessage('timer', 'show')
      } else {
        this.show_timer = false
        this.sendWsMessage('timer', 'hide')
      }
      // Send an immediate sync so any in-flight stale sync is overwritten
      if (this.gameConfig.auto_sync) {
        this.sendWsMessage('sync', {
          timer: this.hours + ':' + this.minutes + ':' + this.seconds,
          clues_used: this.clues_used,
          clues_remaining: this.clues_remaining,
          timer_active: this.timerEnabled,
          show_timer: this.show_timer
        })
      }
    },

    overrideTimer() {
      this.pauseTimer()
      this.time_override = false
      const hours = Math.floor(this.timer_override_value / 60)
      const remainingMinutes = this.timer_override_value % 60
      this.hours = ('0' + hours).slice(-2)
      this.minutes = ('0' + remainingMinutes).slice(-2)
      this.seconds = '00'
      this.totalSeconds = (parseInt(this.hours) * 3600) + (parseInt(this.minutes) * 60)
      this.originalTotalSeconds = this.totalSeconds + 2
      this.originalTotalMinutes = Math.ceil(this.totalSeconds / 60)

      let newTimeValue = this.hours + ':' + this.minutes + ':00'
      this.sendWsMessage('new_timer_value', newTimeValue)
    },

    adjustTimer(minuteDelta) {
      const deltaSeconds = minuteDelta * 60
      this.totalSeconds += deltaSeconds

      if (!this.countUpMode && this.totalSeconds < 0) {
        this.totalSeconds = 0
      }

      this.updateTimeDisplay()

      let newTimeValue = this.hours + ':' + this.minutes + ':' + this.seconds
      this.sendWsMessage('adjust_timer', newTimeValue)

      this.$emit('timer-adjusted', {
        totalSeconds: this.totalSeconds,
        display: newTimeValue
      })
    },

    setTimerSpeed(speed) {
      this.timerSpeed = speed
      this.sendWsMessage('timer_speed', speed)
      this.$emit('timer-speed-changed', speed)
    },

    getTimerValue() {
      return this.hours + ':' + this.minutes + ':' + this.seconds
    },

    // --- Hint sending ---
    sendCustomClue() {
      if (!this.custom_clue || this.custom_clue.trim() === '') return

      this.sendWsMessage('custom', this.custom_clue, { count_as_hint: 1 })
      this.$emit('clue-sent', {
        content: this.custom_clue,
        type: 'custom',
        clueId: null,
        countAsHint: 1,
        puzzleId: null
      })
      this.custom_clue = ''
    },

    clearHints(type) {
      this.sendWsMessage('clear', '')
      this.cleared = true
      if (type === 'vid') { this.cleared = false }
    },

    takeBackHint() {
      this.sendWsMessage('remove_hint', '')
    },

    resetHints() {
      this.sendWsMessage('reset_hints', '')
    },

    giveExtraHints() {
      this.sendWsMessage('extra_hint', '')
    },

    removeExtraHints() {
      this.sendWsMessage('remove_extra_hint', '')
    },

    // --- Background music ---
    playBackgroundMusic() {
      this.background_playing = true
      this.sendWsMessage('start_background_music', '')
    },

    pauseBackgroundMusic() {
      this.background_playing = false
      this.sendWsMessage('pause_background_music', '')
    },

    // --- Sync ---
    autoSync() {
      this.sync_disabled = true
      let syncData = {
        timer: this.hours + ':' + this.minutes + ':' + this.seconds,
        clues_used: this.clues_used,
        clues_remaining: this.clues_remaining,
        timer_active: this.timerEnabled,
        show_timer: this.show_timer
      }
      this.sendWsMessage('sync', syncData)

      if (this.gameConfig.auto_sync) {
        setTimeout(this.autoSync, 1000)
      }
    },

    copyURL() {
      navigator.clipboard.writeText(
        'https://console.offthecouch.io/clues/display?gameId=' + this.gameId
      )
      this.$notify({
        type: 'success',
        message: 'Display link copied!',
        icon: 'tim-icons icon-check-2'
      })
    },

    // --- WebSocket ---
    sendWsMessage(type, data, extra) {
      if (!this.socket || this.socket.readyState !== WebSocket.OPEN) return

      let message = {
        action: 'sendclue',
        gameId: this.gameId,
        data: data,
        type: type,
        clue_id: (extra && extra.clue_id) || '',
        count_as_hint: (extra && extra.count_as_hint) || 0,
        loop: (extra && extra.loop) || 0
      }
      this.socket.send(JSON.stringify(message))
    },

    attachSocketListeners() {
      if (!this.socket) return

      // Remove previous listener if any to prevent duplicates
      this.detachSocketListeners()

      this.wsMessageHandler = (event) => {
        this.handleWsMessage(JSON.parse(event.data))
      }
      this.socket.addEventListener('message', this.wsMessageHandler)
    },

    detachSocketListeners() {
      if (this.wsMessageHandler && this.socket) {
        this.socket.removeEventListener('message', this.wsMessageHandler)
      }
      this.wsMessageHandler = null
    },

    handleWsMessage(msg) {
      // Intro video ended - start timer if waiting
      if (msg.type === 'intro_video_ended') {
        this.handleIntroVideoEnded()
        return
      }

      // Timer messages handled locally (timer is in this panel)
      if (msg.type === 'timer') {
        // These are echoes from our own sends - no-op since we handle timer locally
        return
      }

      // Store sent clues (not metadata messages)
      let metaTypes = ['sync', 'timer', 'clear', 'new_timer_value', 'adjust_timer', 'timer_speed',
        'reset_hints', 'extra_hint', 'remove_extra_hint', 'remove_hint',
        'start_background_music', 'pause_background_music', 'intro_video_ended', 'completion_media']

      if (!metaTypes.includes(msg.type)) {
        let now = new Date()
        this.clues_sent.push({
          type: msg.type,
          timestamp: now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          timestamp_value: now,
          hint: msg.data,
          clue_id: msg.clue_id
        })
        this.cleared = false
        if (msg.type === 'video' && msg.loop) {
          this.video_looping = true
        } else {
          this.video_looping = false
        }
        this.h++
      }

      // Hint counter updates
      if (msg.type === 'reset_hints') {
        this.clues_remaining = parseInt(this.gameConfig.max_clues) || 0
        this.clues_used = 0
        this.h++
      }
      if (msg.type === 'remove_hint') {
        if (this.clues_used > 0) {
          this.clues_used--
          this.clues_remaining++
        }
        this.h++
      }
      if (msg.type === 'extra_hint') {
        this.clues_remaining++
        this.h++
      }
      if (msg.type === 'remove_extra_hint') {
        this.clues_remaining = Math.max(0, this.clues_remaining - 1)
        this.h++
      }
      if (msg.type === 'clear') {
        this.cleared = true
      }

      // Track connected clients
      if (msg.connections !== undefined) {
        this.connected_clients = msg.connections
        this.sync_disabled = false
      }
    },

    // --- Hint counter management (called from parent) ---
    decrementHintCounter() {
      if (this.gameConfig.has_clue_limit && this.clues_remaining > 0) {
        this.clues_remaining--
        this.clues_used++
        this.h++
      }
    },

    // --- Widget visibility ---
    shouldShowWidget(name) {
      if (!this.visibleWidgets) return true
      return this.visibleWidgets.includes(name)
    },

    // --- Helpers ---
    getDisplayedHintLabel(item) {
      if (!item || !item.hint) return '-'
      if (item.type !== 'text' && item.type !== 'custom' && item.hint.length > 0) {
        let parts = item.hint.split('/')
        return parts[parts.length - 1]
      }
      return item.hint
    },

    getHintLog() {
      return this.clues_sent
    }
  },
  mounted() {
    if (this.socket) {
      this.attachSocketListeners()
    }
  },
  beforeDestroy() {
    this.stopTimer()
    this.detachSocketListeners()
  }
}
</script>

<style scoped>
.display-control-panel {
  padding: 0;
}

/* ========================================
   Base widget
   ======================================== */
.dcp-widget {
  background: rgba(255, 255, 255, 0.045);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: clamp(10px, 1.2vw, 16px);
  margin-bottom: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.5), 0 1px 3px rgba(0, 0, 0, 0.3);
  overflow: hidden;
}

.dcp-widget__title {
  font-size: 13px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.85);
  margin-bottom: 12px;
}

/* ========================================
   Connection widget
   ======================================== */
.dcp-connection {
  padding: 10px clamp(10px, 1.2vw, 16px);
}

.dcp-connection__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.dcp-connection__status {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
}

.dcp-connection__status--off {
  color: rgba(255, 255, 255, 0.3);
}

.dcp-status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #00bf9a;
  box-shadow: 0 0 6px rgba(0, 191, 154, 0.5);
}

.dcp-status-dot--off {
  background: rgba(255, 255, 255, 0.2);
  box-shadow: none;
}

.dcp-connection__actions {
  display: flex;
  gap: 4px;
}

.dcp-link-btn {
  width: 28px;
  height: 28px;
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: transparent;
  color: rgba(255, 255, 255, 0.5);
  font-size: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
}

.dcp-link-btn:hover {
  border-color: #e14eca;
  color: #e14eca;
}

.dcp-link-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

/* ========================================
   Timer widget
   ======================================== */
.dcp-timer-widget {
  padding: 0;
  overflow: hidden;
  position: relative;
}

.dcp-timer-widget::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, transparent, #e14eca, transparent);
  z-index: 1;
}

.dcp-timer-face {
  background: rgba(0, 0, 0, 0.3);
  padding: 20px clamp(8px, 1vw, 16px) 12px;
  text-align: center;
  transition: background 0.3s ease;
  position: relative;
}

.dcp-timer-face--running {
  background: rgba(0, 0, 0, 0.4);
  box-shadow: 0 0 20px rgba(225, 78, 202, 0.12), inset 0 0 15px rgba(225, 78, 202, 0.05);
}


.dcp-timer-digits {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 2px;
  font-family: 'SF Mono', 'Menlo', 'Monaco', 'Courier New', monospace;
  font-size: clamp(12px, 1.6vw, 36px);
  font-weight: 300;
  letter-spacing: clamp(0px, 0.3vw, 2px);
  color: rgba(255, 255, 255, 0.95);
  line-height: 1;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.dcp-timer-face--running .dcp-timer-digits {
  color: #fff;
  text-shadow: 0 0 20px rgba(225, 78, 202, 0.4), 0 0 40px rgba(225, 78, 202, 0.15);
}

.dcp-timer-segment {
  min-width: 2ch;
  text-align: center;
}

.dcp-timer-colon {
  opacity: 0.5;
  font-weight: 200;
}

.dcp-timer-mode {
  display: block;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  color: rgba(255, 255, 255, 0.35);
  margin-top: 6px;
}

/* Timer control buttons */
.dcp-timer-controls {
  display: flex;
  gap: 6px;
  padding: 12px clamp(8px, 1vw, 16px);
}

.dcp-timer-btn {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 32px;
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.7);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.dcp-timer-btn .tim-icons {
  font-size: 11px;
}

.dcp-timer-btn:hover {
  border-color: rgba(255, 255, 255, 0.3);
  color: #fff;
}

.dcp-timer-btn--play {
  border-color: #e14eca;
  color: #e14eca;
  background: rgba(225, 78, 202, 0.08);
}

.dcp-timer-btn--play:hover {
  background: rgba(225, 78, 202, 0.15);
  border-color: #e14eca;
  color: #e14eca;
}

.dcp-timer-btn--play.active {
  background: rgba(225, 78, 202, 0.2);
  border-color: #e14eca;
  color: #e14eca;
}

.dcp-timer-btn--start-game {
  background: rgba(225, 78, 202, 0.15);
  font-weight: 600;
  flex: 1;
}

.dcp-timer-btn--start-game:hover {
  background: rgba(225, 78, 202, 0.25);
}

.dcp-timer-btn--complete {
  background: rgba(0, 191, 154, 0.15);
  color: #00bf9a;
  border-color: rgba(0, 191, 154, 0.3);
  font-weight: 600;
  flex: 1;
}

.dcp-timer-btn--complete:hover {
  background: rgba(0, 191, 154, 0.25);
  border-color: rgba(0, 191, 154, 0.5);
  color: #00bf9a;
}

/* Secondary text buttons */
.dcp-timer-secondary {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 4px 12px;
  padding: 0 clamp(8px, 1vw, 16px) 12px;
}

.dcp-text-btn {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.4);
  font-size: 11px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 2px 0;
  transition: color 0.15s ease;
  white-space: nowrap;
}

.dcp-text-btn .tim-icons {
  font-size: 11px;
}

.dcp-text-btn:hover {
  color: rgba(255, 255, 255, 0.8);
}

/* Override panel */
.dcp-timer-override {
  padding: 12px clamp(8px, 1vw, 16px);
}

.dcp-timer-override__label {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
  display: block;
}

.dcp-timer-override__row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.dcp-timer-override__row .mb-0 {
  flex: 1;
}

.dcp-timer-override__row .dcp-timer-btn {
  flex: 0 0 auto;
  padding: 0 14px;
  height: 40px;
}

.dcp-timer-override__row .dcp-text-btn {
  flex: 0 0 auto;
}

/* Music widget */
.dcp-music-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.dcp-music-row .dcp-timer-btn {
  flex: 0 0 auto;
  padding: 0 14px;
}

.dcp-music-row .dcp-widget__title {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ========================================
   Send custom hint
   ======================================== */
.dcp-hint-textarea {
  width: 100%;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 4px;
  padding: 10px 12px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 13px;
  resize: vertical;
  min-height: 60px;
  transition: border-color 0.15s ease;
}

.dcp-hint-textarea::placeholder {
  color: rgba(255, 255, 255, 0.25);
}

.dcp-hint-textarea:focus {
  outline: none;
  border-color: rgba(225, 78, 202, 0.4);
}

.dcp-hint-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-top: 8px;
}

.dcp-hint-footer .dcp-timer-btn {
  flex: 0 0 auto;
  padding: 0 14px;
}

.dcp-hint-footer .dcp-timer-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

/* ========================================
   Hint counter
   ======================================== */
.dcp-hint-counter-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.dcp-hint-count-label {
  font-size: 13px;
  font-weight: 600;
  color: #e14eca;
  font-variant-numeric: tabular-nums;
}

.dcp-hint-icons {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
  padding: 14px 0;
}

.dcp-hint-icon {
  font-size: 20px;
  transition: all 0.2s ease;
}

.dcp-hint-icon--used {
  color: #e14eca;
  filter: drop-shadow(0 0 8px rgba(225, 78, 202, 0.6));
}

.dcp-hint-icon--remaining {
  color: rgba(255, 255, 255, 0.35);
}

.dcp-hint-counter-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

.dcp-hint-counter-actions .dcp-link-btn {
  width: 30px;
  height: 30px;
  font-size: 13px;
  border-color: rgba(255, 255, 255, 0.25);
  color: rgba(255, 255, 255, 0.7);
}

.dcp-hint-counter-actions .dcp-text-btn {
  font-size: 12px;
  padding: 4px 10px;
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  color: rgba(255, 255, 255, 0.7);
}

.dcp-hint-counter-actions .dcp-text-btn:hover {
  border-color: rgba(255, 255, 255, 0.35);
  color: #fff;
  background: rgba(255, 255, 255, 0.08);
}

/* ========================================
   On screen / currently displayed
   ======================================== */
.dcp-now-showing__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.dcp-now-showing__content {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 4px;
  padding: 8px 12px;
}

.dcp-now-showing__label {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.8);
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dcp-now-showing__badge {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #e14eca;
  background: rgba(225, 78, 202, 0.12);
  border-radius: 3px;
  padding: 2px 6px;
  flex-shrink: 0;
}

.dcp-now-showing__empty {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.25);
  font-style: italic;
}

/* ========================================
   Time adjustment buttons
   ======================================== */
.dcp-timer-adjust {
  padding: 8px clamp(8px, 1vw, 16px) 4px;
}

.dcp-adjust-title {
  display: block;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: rgba(255, 255, 255, 0.35);
  margin-bottom: 6px;
}

.dcp-adjust-row {
  display: flex;
  gap: 4px;
}

.dcp-adjust-divider {
  width: 1px;
  align-self: stretch;
  background: rgba(255, 255, 255, 0.12);
  flex-shrink: 0;
  margin: 2px 2px;
}

.dcp-adjust-btn {
  flex: 1;
  min-width: 0;
  height: 30px;
  padding: 0;
  border-radius: 4px;
  border: 1px solid transparent;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
  line-height: 30px;
  text-align: center;
  white-space: nowrap;
}

.dcp-adjust-btn--minus {
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.7);
  border-color: rgba(255, 255, 255, 0.1);
}

.dcp-adjust-btn--minus:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.2);
  color: rgba(255, 255, 255, 0.9);
}

.dcp-adjust-btn--plus {
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.7);
  border-color: rgba(255, 255, 255, 0.1);
}

.dcp-adjust-btn--plus:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.2);
  color: rgba(255, 255, 255, 0.9);
}

/* ========================================
   Timer speed controls
   ======================================== */
.dcp-timer-speed {
  padding: 4px clamp(8px, 1vw, 16px) 8px;
}

.dcp-speed-title {
  display: block;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: rgba(255, 255, 255, 0.35);
  margin-bottom: 6px;
}

.dcp-speed-row {
  display: flex;
  gap: 4px;
}

.dcp-speed-btn {
  flex: 1;
  min-width: 0;
  height: 28px;
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.04);
  color: rgba(255, 255, 255, 0.5);
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  padding: 0;
  white-space: nowrap;
}

.dcp-speed-btn:hover {
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.8);
}

.dcp-speed-btn--active {
  background: rgba(225, 78, 202, 0.15);
  border-color: rgba(225, 78, 202, 0.4);
  color: #e14eca;
  font-weight: 600;
}
</style>

<style>
.white-content .dcp-widget {
  background: #fff;
  border-color: rgba(0, 0, 0, 0.1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.white-content .dcp-widget__title {
  color: #344675;
}

.white-content .dcp-timer-face {
  background: rgba(0, 0, 0, 0.04);
}

.white-content .dcp-timer-face--running {
  background: rgba(0, 0, 0, 0.06);
}

.white-content .dcp-timer-digits {
  color: #344675;
}

.white-content .dcp-timer-face--running .dcp-timer-digits {
  color: #1a1a2e;
  text-shadow: 0 0 20px rgba(225, 78, 202, 0.15);
}

.white-content .dcp-timer-mode {
  color: rgba(0, 0, 0, 0.35);
}

.white-content .dcp-timer-btn {
  border-color: rgba(0, 0, 0, 0.12);
  color: rgba(0, 0, 0, 0.5);
  background: rgba(0, 0, 0, 0.03);
}

.white-content .dcp-timer-btn:hover {
  border-color: rgba(0, 0, 0, 0.25);
  color: #344675;
}

.white-content .dcp-timer-btn--play {
  border-color: #e14eca;
  color: #e14eca;
  background: rgba(225, 78, 202, 0.05);
}

.white-content .dcp-text-btn {
  color: rgba(0, 0, 0, 0.35);
}

.white-content .dcp-text-btn:hover {
  color: #344675;
}

.white-content .dcp-connection__status {
  color: rgba(0, 0, 0, 0.5);
}

.white-content .dcp-link-btn {
  border-color: rgba(0, 0, 0, 0.1);
  color: rgba(0, 0, 0, 0.4);
}

.white-content .dcp-link-btn:hover {
  border-color: #e14eca;
  color: #e14eca;
}

.white-content .dcp-status-dot--off {
  background: rgba(0, 0, 0, 0.15);
}

.white-content .dcp-timer-override__label {
  color: rgba(0, 0, 0, 0.5);
}

.white-content .dcp-hint-textarea {
  background: rgba(0, 0, 0, 0.03);
  border-color: rgba(0, 0, 0, 0.12);
  color: #344675;
}

.white-content .dcp-hint-textarea::placeholder {
  color: rgba(0, 0, 0, 0.3);
}

.white-content .dcp-hint-textarea:focus {
  border-color: rgba(225, 78, 202, 0.4);
}

.white-content .dcp-hint-icon--remaining {
  color: rgba(0, 0, 0, 0.2);
}

.white-content .dcp-hint-counter-actions {
  border-top-color: rgba(0, 0, 0, 0.06);
}

.white-content .dcp-hint-counter-actions .dcp-text-btn {
  border-color: rgba(0, 0, 0, 0.1);
  color: rgba(0, 0, 0, 0.45);
}

.white-content .dcp-hint-counter-actions .dcp-text-btn:hover {
  border-color: rgba(0, 0, 0, 0.2);
  color: #344675;
  background: rgba(0, 0, 0, 0.03);
}

.white-content .dcp-hint-counter-actions .dcp-link-btn {
  border-color: rgba(0, 0, 0, 0.12);
}

.white-content .dcp-now-showing__content {
  background: rgba(0, 0, 0, 0.03);
}

.white-content .dcp-now-showing__label {
  color: #344675;
}

.white-content .dcp-now-showing__empty {
  color: rgba(0, 0, 0, 0.3);
}

.white-content .dcp-adjust-title,
.white-content .dcp-speed-title {
  color: rgba(0, 0, 0, 0.35);
}

.white-content .dcp-adjust-divider {
  background: rgba(0, 0, 0, 0.12);
}

.white-content .dcp-adjust-btn--minus {
  background: rgba(0, 0, 0, 0.04);
  color: rgba(0, 0, 0, 0.6);
  border-color: rgba(0, 0, 0, 0.08);
}

.white-content .dcp-adjust-btn--minus:hover,
.white-content .dcp-adjust-btn--plus:hover {
  background: rgba(0, 0, 0, 0.08);
  border-color: rgba(0, 0, 0, 0.12);
  color: rgba(0, 0, 0, 0.8);
}

.white-content .dcp-adjust-btn--plus {
  background: rgba(0, 0, 0, 0.04);
  color: rgba(0, 0, 0, 0.6);
  border-color: rgba(0, 0, 0, 0.08);
}

.white-content .dcp-speed-btn {
  border-color: rgba(0, 0, 0, 0.08);
  background: rgba(0, 0, 0, 0.03);
  color: rgba(0, 0, 0, 0.45);
}

.white-content .dcp-speed-btn:hover {
  background: rgba(0, 0, 0, 0.06);
  color: rgba(0, 0, 0, 0.7);
}

.white-content .dcp-speed-btn--active {
  background: rgba(225, 78, 202, 0.08);
  border-color: rgba(225, 78, 202, 0.3);
  color: #e14eca;
}
</style>
